import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { ActivationStatus } from '../../../extViewmodels/sdk/models';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MastheadPortalId } from '../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IErrorMessageComponentProps,
	IFullscreenModalComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../models/AppState';
import { useAdminAccountQuery } from '../../../queries/Admin/Account/useAdminAccountQuery';
import { DataRefreshViewModel } from '../../../viewmodels/AdminViewModels';
import { IOperationResultNoValue, ImportContactsContentType, TagViewModel } from '../../../viewmodels/AppViewModels';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../web/components-v2/Select';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../web/components/Breadcrumb';
import { Checkbox } from '../../../web/components/Checkbox';
import { ContactsSelfImportMapping } from '../../../web/components/contacts/ContactsSelfImportMapping';
import { ContactsSelfImportProcessing } from '../../../web/components/contacts/ContactsSelfImportProcessing';
import { FilesDropTarget } from '../../../web/components/FilesDropTarget';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { MultiContainerHeader } from '../../../web/components/MultiContainerHeader';
import { SpreadsheetIcon } from '../../../web/components/svgs/icons/SpreadsheetIcon';
import { SuccessCheckIcon } from '../../../web/components/svgs/icons/SuccessCheckIcon';
import { SvgIcon } from '../../../web/components/svgs/icons/SvgIcon';
import { brandSecondary, nameCircles } from '../../../web/styles/colors';
import { baseStyleSheet } from '../../../web/styles/styles';
import { AcceptedFileExtensions } from '../ContactImport';
import { TagSelector } from '../TagSelector';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		IErrorMessageComponentProps,
		IFullscreenModalComponentProps,
		IUserSessionComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	accountId: string;
	accountName: string;
}

type Step = 1 | 2 | 3;

interface IState {
	currentStep?: Step;
	file: File;
}

class _DataRefresh extends React.Component<IProps, IState> {
	private mFilesInputRef: React.RefObject<HTMLInputElement>;
	private vm: DataRefreshViewModel;
	constructor(props: IProps) {
		super(props);

		this.vm = new DataRefreshViewModel(this.props.userSession, { id: this.props.accountId });

		this.mFilesInputRef = React.createRef<HTMLInputElement>();
		this.state = {
			currentStep: 1,

			file: null,
		};
	}

	public async componentDidMount() {
		try {
			await this.vm.loadPolicyTags();
			await this.vm.accountUsers.loadUsers();
		} catch (err) {
			const { errorMessages, logApiError } = this.props;

			errorMessages.pushApiError(err);

			logApiError('AdminDataRefreshLoad-Error', err);
		}
	}

	private renderMapping() {
		return (
			<ContactsSelfImportMapping
				className='csv-import-mapping'
				importContacts={this.vm}
				onCancelButtonClicked={this.onCancel}
				showMergeByNameCheckbox={true}
			/>
		);
	}

	private renderProcessing() {
		return (
			<ContactsSelfImportProcessing
				className={css(styleSheet.processing)}
				importContacts={this.vm}
				onRequestClose={this.onCancel}
			/>
		);
	}

	private onCancel = () => {
		const { fullscreenModal } = this.props;

		fullscreenModal.dismissModal();
	};

	public render() {
		const { className, styles } = this.props;
		const { currentStep } = this.state;

		if (!this.vm) {
			return null;
		}

		if (this.vm.systemJob) {
			return this.renderProcessing();
		}

		if (this.vm.preview) {
			return this.renderMapping();
		}

		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} data-refresh ${className || ''}`}>
				<MultiContainerHeader
					appBarHeader={
						<Breadcrumb>
							<BreadcrumbList>
								<BreadcrumbItem>
									<BreadcrumbLink asChild>
										<NavLink to='/accounts'>Accounts</NavLink>
									</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbSeparator />
								<BreadcrumbItem>
									<BreadcrumbLink asChild>
										<NavLink to={`/accounts/${this.props.accountId}?tab=Imports`}>
											{this.props.accountName ?? 'Loading...'}
										</NavLink>
									</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbSeparator />
								<BreadcrumbItem>
									<BreadcrumbPage>Import Data Refresh</BreadcrumbPage>
								</BreadcrumbItem>
							</BreadcrumbList>
						</Breadcrumb>
					}
					appBarPortalDestinationId={MastheadPortalId}
				/>
				<div className={css(styleSheet.title)}>Refresh Insurance Data</div>
				<StepSection
					step={1}
					currentStep={currentStep}
					title={<span>Step 1: Decide whether you&apos;d like to clear the renewal dates (recommended)</span>}
				>
					<Checkbox
						id='clear-keyfacts-checkbox'
						type='large'
						onChange={this.onChangeClearRenewalKeyFacts}
						checked={this.vm.clearRenewalKeyFacts}
					>
						<div className={css(styleSheet.clearRenewalKeyFactsText)}>Clear renewal date key facts</div>
					</Checkbox>
				</StepSection>
				{currentStep === 1 && this.renderStepButton()}
				<StepSection
					step={2}
					currentStep={currentStep}
					title={<span>Step 2: Select the policy tags you&apos;d like to delete</span>}
				>
					<TagSelector
						onClearSelectedTags={this.onCollectTagsToDelete}
						dataRefreshVm={this.vm}
						withPolicyTags={!!this.vm.policyTags?.length}
					/>
				</StepSection>
				{currentStep === 2 && this.renderStepButton()}
				<StepSection
					step={3}
					currentStep={currentStep}
					title={
						<div className={css(styleSheet.selectStepTitle)}>
							Step 3: Upload a new spreadsheet for
							<Select value={this.vm.selectedUser?.id ?? ''} onValueChange={this.onUserChanged}>
								<SelectTrigger styles={styleSheet.userSelect}>
									<span>{this.vm.selectedUser?.name ?? 'Select a user'}</span>
								</SelectTrigger>
								<SelectContent>
									{(this.vm.accountUsers?.accountUsers || [])
										.filter(user => user.activationStatus !== ActivationStatus.DEACTIVATED)
										.map(user => (
											<SelectItem key={user.id} value={user.id} title={user.primaryEmail?.value}>
												{user.name}
											</SelectItem>
										))}
								</SelectContent>
							</Select>
							and make contacts
							<Select value={this.vm.shareImports ? 'shared' : 'private'} onValueChange={this.onShareChanged}>
								<SelectTrigger styles={styleSheet.userSelect}>
									<span>{this.vm.shareImports ? 'Shared' : 'Private'}</span>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value='private'>Private</SelectItem>
									<SelectItem value='shared'>Shared</SelectItem>
								</SelectContent>
							</Select>
						</div>
					}
				>
					{this.renderFileChooser()}
				</StepSection>
				{currentStep === 3 && this.renderStepButton()}
			</div>
		);
	}

	private renderStepButton() {
		const { currentStep } = this.state;
		return (
			<div className={css(styleSheet.nextButtonSection)}>
				<button className={css(baseStyleSheet.ctaButton)} onClick={this.nextStep()}>
					<span>{currentStep === 3 ? 'Import' : 'Next step'}</span>
				</button>
			</div>
		);
	}

	private onShareChanged = (value: string) => {
		this.vm.shareImports = value === 'shared';
	};

	private onUserChanged = (value: string) => {
		this.vm.selectedUser = this.vm.accountUsers?.accountUsers?.find(x => x.id === value);
	};

	private onCollectTagsToDelete = (tags: TagViewModel[]) => {
		this.vm.tagsToDelete.addAll(tags);
	};

	private nextStep = () => () => {
		const { logApiError, errorMessages } = this.props;
		const { currentStep, file } = this.state;
		if (currentStep === 3) {
			if (!this.vm.selectedUser) {
				toast.error('You must select a user to import for.');
				return;
			}
			if (!file) {
				toast.error('You must select a file to import.');
				return;
			}

			const fileExt = (file.name || '')
				.split('.')
				.filter(x => !!x)
				.reverse()[0]
				.toLocaleLowerCase();
			switch (fileExt) {
				case 'xlsx': {
					this.vm.options.setContentType(ImportContactsContentType.xlsx);
					break;
				}
				case 'xls': {
					this.vm.options.setContentType(ImportContactsContentType.xls);
					break;
				}
				default: {
					this.vm.options.setContentType(ImportContactsContentType.csv);
					break;
				}
			}
			this.vm.importFromFile(file)?.catch((err: IOperationResultNoValue) => {
				logApiError('DataRefreshImportPreview-Error', err);

				errorMessages.pushApiError(err);
			});
		} else {
			this.setState({ currentStep: (currentStep + 1) as Step });
		}
	};

	private onChangeClearRenewalKeyFacts = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.vm.clearRenewalKeyFacts = e.target.checked;
	};

	private onFileDropped = (files: File[]) => {
		const { logInput } = this.props;
		if (files.length > 0) {
			const file = files[0];
			const fileExt = ((file ? file.name : '')
				.split('.')
				.filter(x => !!x)
				.reverse() || [])[0];

			logInput('FileDropped', 'Direct', { fileExt });
			this.setState({ file });
		}
	};

	private onFilesInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { logEvent } = this.props;
		const files = e.target.files;
		if (!!files && files.length > 0) {
			const filteredFiles = Array.from(files).filter(x => {
				const comps = x.name ? x.name.split('.').filter(y => !!y) : [];
				const ext = comps.length > 1 ? comps[comps.length - 1] : null;
				return ext ? AcceptedFileExtensions.indexOf(ext.toLocaleLowerCase()) >= 0 : false;
			});

			e.target.value = null;

			if (filteredFiles.length > 0) {
				const file = filteredFiles[0];
				const fileExt = ((file ? file.name : '')
					.split('.')
					.filter(x => !!x)
					.reverse() || [])[0];

				logEvent('FilesAdded', { fileExt });
				this.setState({
					file,
				});
			}
		}
	};

	private onChooseFileButtonClicked = () => {
		if (this.mFilesInputRef.current) {
			const { logInput } = this.props;

			logInput('ChooseFile', 'Click');
			this.mFilesInputRef.current.click();
		}
	};

	private renderFileChooser() {
		const { file } = this.state;
		const uploadHint = `Supported file types: ${AcceptedFileExtensions.split(',').reduce((x, y) => {
			return `${x}${x ? ', ' : ''}${y}`;
		}, '')}`;
		return (
			<div className={css(styleSheet.uploadField)}>
				{this.vm.isBusy ? (
					<LoadingSpinner className='absolute-center' type='large' />
				) : (
					<FilesDropTarget
						className={css(styleSheet.uploadTarget)}
						onFilesDropped={this.onFileDropped}
						supportedFileExtensions={AcceptedFileExtensions.split(',').map(x => x.slice(1))}
						supportMultiple={true}
						title={uploadHint}
					>
						<div className={css(styleSheet.uploadTargetOverlay)}>
							<SpreadsheetIcon className={css(styleSheet.uploadSpreadsheetIcon)} />
							{file ? (
								<div className={css(styleSheet.uploadTargetOverlayChosenFile)}>{file.name}</div>
							) : (
								<SvgIcon className={css(styleSheet.uploadTargetOverlayArrow)} height={27} width={20}>
									<polygon
										fill={brandSecondary}
										fillRule='evenodd'
										points='20 10.048 10 0 0 10.048 3.004 13.066 7.876 8.172 7.876 26.5 12.124 26.5 12.124 8.172 16.996 13.066'
									/>
								</SvgIcon>
							)}
							<input
								accept={AcceptedFileExtensions}
								id='contacts-self-import-upload-files-input'
								multiple={false}
								onChange={this.onFilesInputChanged}
								ref={this.mFilesInputRef}
								style={{
									height: 0,
									opacity: 0,
									pointerEvents: 'none',
									position: 'fixed',
									visibility: 'hidden',
									width: 0,
									zIndex: -1,
								}}
								type='file'
							/>
							<div className={css(styleSheet.uploadTargetOverlayMessage)}>
								<span>Drop any Excel, CSV, or</span>
								<button
									className={css(baseStyleSheet.brandSecondaryLink, styleSheet.uploadTargetOverlayButton)}
									onClick={this.onChooseFileButtonClicked}
								>
									&nbsp;
									<span>browse your files</span>
								</button>
							</div>
						</div>
					</FilesDropTarget>
				)}
			</div>
		);
	}
}

const DataRefreshAsObserver = observer(_DataRefresh);
const DataRefreshWithContext = inject(
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	UserSessionViewModelKey
)(DataRefreshAsObserver);
const DataRefreshWithLogging = withEventLogging(DataRefreshWithContext, 'DataRefresh');
export const DataRefresh = (props: Omit<IProps, 'accountId' | 'accountName'>) => {
	const params = useParams<{ accountId: string }>();
	const accountQuery = useAdminAccountQuery({ accountId: params.accountId });
	return (
		<DataRefreshWithLogging {...props} accountId={params.accountId} accountName={accountQuery.data?.companyName} />
	);
};

interface IStepProps {
	currentStep?: number;
	step?: number;
	title?: JSX.Element;
}

const StepSection: React.FC<React.PropsWithChildren<IStepProps>> = props => {
	const { step, children, currentStep, title } = props;

	const checked = currentStep >= step;
	const grayed = !checked;
	return (
		<div className={css(styleSheet.stepSection, grayed && styleSheet.grayed)}>
			<div className={css(styleSheet.stepTitle)}>
				<SuccessCheckIcon fillColor={checked ? null : nameCircles} ringColor={checked ? null : '#F9F9F9'} />
				{title}
			</div>
			<div className={css(styleSheet.stepContent)}>{children}</div>
		</div>
	);
};
